import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navigation, Loader2, MapPin } from 'lucide-react';
import { geocoderApi, ResolvedAddress } from '../services/geocoderApi';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import type { DeliveryAddress } from '../services/ordersApi';

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

interface Props {
  currentDeliveryAddress: DeliveryAddress | null;
  onAccept: (address: DeliveryAddress, terminalGroupId?: string) => void;
}

export function LocationSuggestionBanner({ currentDeliveryAddress, onAccept }: Props) {
  const { loading, denied, request } = useCurrentLocation();
  const [suggestion, setSuggestion] = useState<{ resolved: ResolvedAddress; isNearSaved: boolean } | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleFindMe = useCallback(async () => {
    setResolving(true);
    setSuggestion(null);
    setResolveError(false);
    const coords = await request();
    if (!coords) { setResolving(false); return; }

    const resolved = await geocoderApi.resolveByCoords(coords.lat, coords.lng);
    setResolving(false);
    if (!resolved) {
      setResolveError(true);
      return;
    }

    let isNearSaved = false;
    if (
      currentDeliveryAddress &&
      typeof currentDeliveryAddress === 'object' &&
      'lat' in currentDeliveryAddress &&
      'lng' in currentDeliveryAddress
    ) {
      const saved = currentDeliveryAddress as any;
      if (saved.lat && saved.lng) {
        const dist = haversineKm(coords.lat, coords.lng, Number(saved.lat), Number(saved.lng));
        isNearSaved = dist < 1;
      }
    }

    setSuggestion({ resolved, isNearSaved });
  }, [request, currentDeliveryAddress]);

  const handleAccept = useCallback(() => {
    if (!suggestion) return;
    const addr: DeliveryAddress = {
      city: suggestion.resolved.city,
      street: suggestion.resolved.street,
      house: suggestion.resolved.house,
      yandexAddressId: suggestion.resolved.yandexAddressId,
      formattedAddress: suggestion.resolved.formattedAddress,
    };
    onAccept(addr);
    setSuggestion(null);
    setDismissed(true);
  }, [suggestion, onAccept]);

  if (dismissed) return null;

  const isLoading = loading || resolving;
  const label = suggestion
    ? suggestion.isNearSaved
      ? `Вы рядом: ${currentDeliveryAddress?.formattedAddress || suggestion.resolved.formattedAddress}`
      : `Вы здесь: ${suggestion.resolved.formattedAddress}`
    : denied
      ? 'Геолокация заблокирована'
      : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        className="mx-4 mb-2 rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(255,255,255,0.9)',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}
      >
        {!suggestion ? (
          <button
            type="button"
            onClick={handleFindMe}
            disabled={isLoading || denied}
            className="w-full flex items-center gap-3 px-4 py-3 transition-all active:opacity-70 disabled:opacity-50"
          >
            {isLoading
              ? <Loader2 size={16} className="animate-spin shrink-0" style={{ color: '#aaa' }} />
              : <Navigation size={16} className="shrink-0" style={{ color: denied ? '#aaa' : '#0a0a0a' }} />
            }
            <span className="text-sm font-medium text-left flex-1" style={{ color: denied ? '#aaa' : '#1a1a1a' }}>
              {denied
                ? 'Геолокация заблокирована'
                : resolveError
                  ? 'Не удалось определить адрес по координатам. Отключите VPN или введите адрес вручную'
                  : isLoading
                    ? 'Определяем местоположение...'
                    : 'Найти меня'}
            </span>
          </button>
        ) : (
          <div className="px-4 py-3">
            <div className="flex items-start gap-2 mb-2">
              <MapPin size={15} className="shrink-0 mt-0.5" style={{ color: '#0a0a0a' }} />
              <p className="text-sm font-medium flex-1" style={{ color: '#1a1a1a', lineHeight: '1.4' }}>
                {label}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAccept}
                className="flex-1 py-2 rounded-xl text-sm font-semibold transition-all active:scale-95"
                style={{ background: '#0a0a0a', color: '#fff' }}
              >
                {suggestion.isNearSaved ? 'Использовать' : 'Доставить сюда'}
              </button>
              <button
                onClick={() => setSuggestion(null)}
                className="py-2 px-4 rounded-xl text-sm font-medium transition-all active:scale-95"
                style={{ background: '#f0f0f0', color: '#666' }}
              >
                Нет
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
